/* eslint-disable import/no-extraneous-dependencies */
import {
  BreadCrumb,
  BreadCrumbElementTypes,
  BreadCrumbs,
  BreadCrumbsSizesMD,
  BreadCrumbsSizesSM,
} from '@picsart/design-system/components/BreadCrumbs';
import { HorizontalContainer } from '@picsart/design-system/components/HorizontalContainer';
import classNames from 'classnames';
import JSONLinkedData from 'components/JSONLinkedData';
import { BreadcrumbSchemaDataModel, SchemaType } from 'components/JSONLinkedData/types';
import { LandingCategory } from 'constants/enums';
import { PageType } from 'constants/pages';
import usePageSlug from 'hooks/usePageSlug';
import { useEffect, useState } from 'react';
import { ColorSchemes } from 'redesignedComponents/context/colorScheme/enums';
import useLandingPropsStore from 'stores/landingPropsStore';
import usePageDataStore from 'stores/pageDataStore';
import { ColorScheme, StrapiDataModel, StrapiHubModel } from 'strapi/types';
import { addColorBreadcrumbs } from './colorBreadcrumbs';
import useStyles from './styles';
import { BreadcrumbModel } from './types';

function Breadcrumb({ className }: { className?: string }) {
  const isMobile = useLandingPropsStore(x => x.isMobile);
  const colorData = usePageDataStore(store => store.colorData);
  const { container, wrapper, dark, darkBreadcrumb, breadcrumbsRoot } = useStyles({
    isMobile,
    isColor: !!colorData?.hex,
  });
  const { pageData, pageType } = usePageDataStore(store => store);
  const { slug } = usePageSlug();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbSchemaDataModel[]>([]);
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const isFontPage = usePageDataStore(
    store => store.pageData?.data?.category === LandingCategory.fonts,
  );
  const isTemplatesPage = usePageDataStore(
    store => store.pageData?.data?.category === LandingCategory.template,
  );

  const pageTitle = usePageDataStore(store => store.pageData?.data?.name || 'Fonts');
  const pageDescription = usePageDataStore(
    store => store.pageData?.data?.seoSettings?.metaDescription,
  );
  useEffect(() => {
    const excludedRoutes = ['not-found'];
    const data = (pageData?.data || pageData) as StrapiDataModel & StrapiHubModel;

    if (data && !data.breadcrumb?.hide && slug && !excludedRoutes.includes(slug)) {
      let breadcrumbItems: BreadcrumbModel[] = [];

      if (data.Components?.[0]?.colorScheme === ColorSchemes.DARK) {
        setDarkMode(true);
      }
      if (data.breadcrumb?.colorScheme) {
        switch (data.breadcrumb.colorScheme) {
          case ColorScheme.light:
            setDarkMode(false);
            break;
          case ColorScheme.dark:
            setDarkMode(true);
            break;
          default:
            break;
        }
      }

      // home part - 1-st breadcrumb
      const root = { anchor: 'Home', link: '/' };
      breadcrumbItems.push(root);

      if (pageType === PageType.Color) {
        breadcrumbItems = addColorBreadcrumbs(
          breadcrumbItems,
          data.breadcrumb?.name || data.name || data.title,
        );

        breadcrumbItems = breadcrumbItems.filter(
          (item, index, self) =>
            index === self.findIndex(t => t.anchor.toLowerCase() === item.anchor.toLowerCase()),
        );
      } else {
        // hub part - 2-nd breadcrumb

        if (data.hubs?.[0]) {
          const parentBreadcrumb = {
            anchor: data.hubs[0].breadcrumb?.name || data.hubs[0].title,
            link: data.hubs[0].breadcrumb?.link || `/${data.hubs[0].slug}/`,
          };
          breadcrumbItems.push(parentBreadcrumb);
        }

        // parent landing - 3-rd breadcrumb
        if (data.parentLanding) {
          const parentLanding = {
            anchor: data.parentLanding.breadcrumb?.name || data.parentLanding.name,
            link: data.parentLanding.breadcrumb?.link || `/${data.parentLanding.slug}/`,
          };
          breadcrumbItems.push(parentLanding);
        }

        // current page - 4-th breadcrumb
        const currentPage = {
          anchor: data.breadcrumb?.name || data.name || data.title,
        };
        breadcrumbItems.push(currentPage);
      }

      setBreadcrumbs(
        breadcrumbItems.map((item, index) => ({
          position: index + 1,
          anchor: item.anchor,
          link: item.link,
        })),
      );
    }
  }, [isFontPage, pageData, pageType, slug]);
  const size = isMobile ? BreadCrumbsSizesSM : BreadCrumbsSizesMD;

  return (
    breadcrumbs &&
    breadcrumbs.length > 0 && (
      <div
        className={classNames(container, darkMode && dark, className)}
        data-pulse-section-group="breadcrumb"
      >
        <HorizontalContainer className={wrapper}>
          <JSONLinkedData data={breadcrumbs} type={SchemaType.Breadcrumb} />
          {(isTemplatesPage || isFontPage) && (
            <JSONLinkedData data={breadcrumbs} type={SchemaType.ItemList} />
          )}
          {isFontPage && (
            <JSONLinkedData
              data={{
                ratingCount: '361',
                ratingValue: '4.9',
                title: pageTitle,
                description: pageDescription,
                pathname: window.location.pathname,
              }}
              type={SchemaType.AggregateRating}
            />
          )}
          <BreadCrumbs
            size={size}
            tag={BreadCrumbElementTypes.Link}
            className={classNames(breadcrumbsRoot, darkMode && darkBreadcrumb)}
          >
            {breadcrumbs.map((item, index) => (
              <BreadCrumb
                key={item.anchor}
                isActive={index === breadcrumbs.length - 1}
                href={item.link}
                tag={BreadCrumbElementTypes.Link}
                size={size}
              >
                {item.anchor}
              </BreadCrumb>
            ))}
          </BreadCrumbs>
        </HorizontalContainer>
      </div>
    )
  );
}

export default Breadcrumb;
